import { useMemo, useState } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
// img
import { generatePath, useNavigate, useParams } from "react-router-dom";
import KhanReportLogo from "components/shared/ReportComponents/KhanReortLogo";
import { useGetFeedbackStepDetailQuery, useSubmitFeedbackStepMutation } from "store/services/clients/assessmentTreatmentPlan";
import { formatReportFeedback, reportInitialValues } from "helpers/assessment";
import { object } from "yup";
import { reportQuestionsValidationSchema } from "utils/validation/validationHelpers";
import { DISCHARGE_SCREENING_REPORT_PREVIEW } from "config/paths";
import { Formik } from "formik";
import { CalendarIcon } from "Assets/icons";
import LoadingIndicator from "components/shared/LoadingIndicator";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingOverlay from "components/shared/LoadingOverlay";
import { dateFormat, getAge, userFullName } from "helpers/common";
import { RenderQuestionById } from "components/shared/ReportComponents/Questions/Index";
import { CLIENT_REPORT_TYPES, DISCHARGE_SCREENING_REPORT_QUESTIONS, DISCIPLINE_REPORT_NAME_MAPPING, REPORT_IDS } from "config/constants";
import AttachDocumentsReports from "components/shared/ReportComponents/AttachDocumentsReports";
import TherapistSignature from "components/shared/ReportComponents/TherapistSignature";
import LoadingButton from "components/shared/LoadingButton";
import GuardianDetailRow from "components/shared/ReportComponents/GuardianDetailRow";
import ReportHeader from "components/shared/ReportComponents/ReportHeader/Index";
import moment from "moment";
import ReportError from "../components/ReportError";

const DischargeScreeningReport = () => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const handleChange = (checked) => {
    setChecked(checked);
  };

  const { assessmentId, clientId, clientType } = useParams();

  const [reportHeading, setReportHeading] = useState(CLIENT_REPORT_TYPES[REPORT_IDS.DISCHARGE_SCREENING_REPORT].title);

  const [submitStep, { isLoading: submittingStep }] = useSubmitFeedbackStepMutation();
  const { data, isLoading, isFetching, isError, refetch } = useGetFeedbackStepDetailQuery({ assessmentId, clientId, clientType });
  const assessmentGoals = data?.assesmentData?.reportGoals;
  const assessmentAssignedId = data?.assesment?.id;
  const reportQuestions = data?.assesmentData?.reportQuestions;

  const caseDiscipline = data?.clientPendingCaseload?.clientCaseloadTherapist?.caseDiscipline;
  const caseTherapist = data?.clientPendingCaseload?.clientCaseloadTherapist?.clientCaseloadMapping?.find(x => x.caseTherapist?.therapist_detail?.type == "veteran")?.caseTherapist;
  const therapistDetails = { caseDiscipline, caseTherapist, discipline_id: data?.clientPendingCaseload?.clientCaseloadTherapist?.discipline_id }

  const initialValues = useMemo(() => {
    if (!data || !reportQuestions) return {};
    let answers = data?.assesmentData?.reportAnswers;
    let values = {
      documents: data?.assesmentData?.reportDocuments,
      resources: data?.assesmentData?.reportResources,
    };

    values = reportInitialValues(values, reportQuestions, answers);

    return values;
  }, [data, reportQuestions]);

  const screeningTemplateData = useMemo(() => {
    if (!data || !therapistDetails) {
      return {};
    }

    const disciplineId = therapistDetails?.discipline_id;
    const disciplineName = DISCIPLINE_REPORT_NAME_MAPPING[disciplineId] || therapistDetails?.caseDiscipline?.name;

    let output = {
      CLIENT_FIRST_NAME: data?.firstname,
      CLIENT_LAST_NAME: data?.lastname,
      DATE: dateFormat(new Date()),
      THERAPIST_FULL_NAME: userFullName(therapistDetails?.caseTherapist),
      THERAPIST_DISCIPLINE: disciplineName,
    }
    return output;
  }, [data, therapistDetails])

  // console.log("initialValues", initialValues)

  const validationSchema = useMemo(() => {
    if (!reportQuestions) return object();

    return reportQuestionsValidationSchema(reportQuestions);
  }, [reportQuestions])

  const handleSubmitStep = async (values) => {
    try {
      let payload = formatReportFeedback(reportHeading, reportQuestions, values);

      let response = await submitStep({
        assessmentAssignedId,
        data: payload,
        clientType
      });

      if (!response?.error) {
        navigate(generatePath(DISCHARGE_SCREENING_REPORT_PREVIEW, {
          assessmentId,
          clientId,
          clientType
        }));
      }

    } catch (err) {
      console.error("Error submitting step three", err);
    }
  };

  return (
    <>
      <section className="ReportSec py-3 position-relative">
        <Container fluid>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmitStep}
            validationSchema={validationSchema}
            enableReinitialize
          >
            {({ handleSubmit, values, errors }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col lg="12" className="my-2">
                    <div className="cardCstm p-lg-5 rounded-2 bg-white shadow">
                      {isLoading ? (
                        <LoadingIndicator />
                      ) : isError ? (
                        <ErrorComponent />
                      ) : (
                        <>
                          {isFetching && <LoadingOverlay />}
                          <ReportHeader
                            initialHeading={data?.assesment?.report_heading || reportHeading}
                            therapistDetails={therapistDetails}
                            setHeading={setReportHeading}
                          />
                          <div className="formBody pt-3">
                            <div
                              className=""
                            // style={{ borderBottom: "2px dashed #6c6c6c" }}
                            >
                              <div className="py-3">
                                <div
                                  className="cardCstm rounded-2 px-3"
                                  style={{ background: "#F9F9F9" }}
                                >
                                  <Row className="border-bottom p-2">
                                    <Col md="6" className="py-2">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          Last Name
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          {data?.lastname}
                                        </p>
                                      </div>
                                    </Col>
                                    <Col md="6" className="py-2">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          First Name
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          {data?.firstname}
                                        </p>
                                      </div>
                                    </Col>
                                  </Row>
                                  <Row className="border-bottom p-2">
                                    <Col md="6" className="py-2">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          Date of Birth:
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          <span className="icn me-2">
                                            <CalendarIcon />
                                          </span>
                                          {dateFormat(data?.client_detail?.dob)}
                                        </p>
                                      </div>
                                    </Col>
                                    <Col md="6" className="py-2">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          Age:
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          {getAge(data?.client_detail?.dob)}
                                        </p>
                                      </div>
                                    </Col>
                                  </Row>
                                  <Row className="border-bottom p-2">
                                    <Col md="6" className="py-2">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          Clinician Name:
                                        </h6>
                                        <p className="m-0 themeBlue text-capitalize">{userFullName(therapistDetails?.caseTherapist)}</p>
                                      </div>
                                    </Col>
                                    <Col md="6" className="py-2">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          Discipline Name:
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          {therapistDetails?.caseDiscipline?.name}
                                        </p>
                                      </div>
                                    </Col>
                                  </Row>
                                  <Row className="border-bottom p-2">
                                    <Col md="6" className="py-2">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          Screening Date:
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          <span className="icn me-2">
                                            <CalendarIcon />
                                          </span>
                                          {dateFormat(moment())}
                                        </p>
                                      </div>
                                    </Col>
                                    {/* <Col md="6" className="py-2">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          Teacher Name:
                                        </h6>
                                        <p className="m-0 themeBlue">John Wangdu</p>
                                      </div>
                                    </Col> */}
                                  </Row>
                                  <Row className="border-bottom p-2">
                                    {clientType == "school" && <Col md="6" className="py-2">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          School Name:
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          {data?.client_detail?.client_school_detail?.client_school?.name}
                                        </p>
                                      </div>
                                    </Col>}
                                    <Col md="6" className="py-2">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          City/Town:
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          {data?.client_detail?.city_town}
                                        </p>
                                      </div>
                                    </Col>
                                  </Row>
                                  {/* Guardian Details */}
                                  {data?.client_detail?.client_guardian_mapping?.length > 0 &&
                                    data?.client_detail?.client_guardian_mapping?.map(
                                      (guardian, index) => {
                                        const slug = guardian?.client_guardian?.client_role?.slug;
                                        if (slug !== (clientType == "school" ? "teacher" : "parent")) {
                                          return null;
                                        }

                                        return (
                                          <GuardianDetailRow
                                            index={index}
                                            guardian={guardian}
                                            slug={slug}
                                          />
                                        );
                                      }
                                  )}
                                </div>
                              </div>
                              <div className="py-3">
                                <RenderQuestionById
                                  questionId={DISCHARGE_SCREENING_REPORT_QUESTIONS.screening_results}
                                  reportQuestions={reportQuestions}
                                  fieldData={screeningTemplateData}
                                />
                              </div>
                              <div className="py-3">
                                <RenderQuestionById
                                  questionId={DISCHARGE_SCREENING_REPORT_QUESTIONS.greeting}
                                  reportQuestions={reportQuestions}
                                  showQuestion={false}
                                />
                              </div>
                              <div className="py-3">
                                <AttachDocumentsReports
                                  acl={"private"}
                                  name="documents"
                                  allowedTypes={["pdf", "doc", "docx", "jpg", "xlsx", "png"]}
                                />
                              </div>
                            </div>
                            <div className="">
                              <div className="py-3">
                                <TherapistSignature
                                  therapistDetails={therapistDetails}
                                />
                              </div>
                              <div className="py-3">
                                <KhanReportLogo />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </Col>
                  <Col lg="12" className="my-2">
                    <ReportError />
                    <LoadingButton
                      type="submit"
                      className="d-flex align-items-center justify-content-center commonBtn"
                      isLoading={submittingStep}
                    >
                      Preview
                    </LoadingButton>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </Container>
      </section>
    </>
  );
};

export default DischargeScreeningReport;
